import { BaseQueryParams } from 'types/base-query-params';
import { PaginatedResponse } from 'types/dtos';
import { ResponseProductCategoryDto } from '../types/dtos';
import { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query';
import { constructQueryParams } from 'utils/query-params-builder';
import { APP_ENDPOINTS } from 'app/endpoints';
import { store } from 'store/index';
import { handleDuplicates, removeUndefined } from 'utils/array-helpers';
import { checkError, fetchResponse } from 'utils/api-helpers';
import { Document } from 'services/documents/types/dtos';
import { formatDate } from 'utils/date-helpers';

export type ProductCategoriesQueryParams = BaseQueryParams & {
    website_id?: string;
};

export default (build: EndpointBuilder<BaseQueryFn, string, string>) =>
    build.query<PaginatedResponse<ResponseProductCategoryDto>, ProductCategoriesQueryParams>({
        async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
            const websiteId = store.getState().workspace.websiteId;
            const params: ProductCategoriesQueryParams = { ..._arg, website_id: websiteId };
            const productCategoriesResponse = await fetchWithBQ(
                constructQueryParams(APP_ENDPOINTS.PRODUCT_CATEGORIES, params),
            );

            if (productCategoriesResponse.error) {
                return { error: productCategoriesResponse.error };
            }
            const productCategories = productCategoriesResponse.data as PaginatedResponse<ResponseProductCategoryDto>;

            let documents: PaginatedResponse<Document>;
            const documentIds = handleDuplicates(
                removeUndefined(productCategories.content.map((document) => document.documentId)),
            );
            if (documentIds.length > 0) {
                const documentResponse = await fetchResponse(APP_ENDPOINTS.DOCUMENTS, documentIds, fetchWithBQ);
                const documentError = checkError(documentResponse);
                if (documentError) return documentError;
                documents = documentResponse.data as PaginatedResponse<Document>;
            }

            productCategories.content = productCategories.content.map((productCategory) => {
                const filePath = documents?.content.find((document) => {
                    return document.id === productCategory.documentId;
                })?.filePaths;

                const createdAt = formatDate(productCategory.createdAt);
                const imageUrl = filePath && JSON.parse(filePath).small;

                return {
                    ...productCategory,
                    imageUrl,
                    createdAt,
                };
            });
            return {
                data: productCategories,
            };
        },
        providesTags: [{ type: 'product-categories-with-join', id: 'GET-JOIN' }],
    });
